import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

function HikeReport() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [hikes, setHikes] = useState([]);
  const [selectedHikeId, setSelectedHikeId] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchHikes();
  }, []);

  useEffect(() => {
    if (selectedHikeId) {
      fetchStats();
    }
  }, [selectedHikeId]);

  const fetchHikes = async () => {
    try {
      const response = await fetch('/api/hikes', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch hikes');
      }

      const data = await response.json();
      setHikes(data);
      
      // Auto-select first hike if available
      if (data.length > 0 && !selectedHikeId) {
        setSelectedHikeId(data[0]._id);
      }
    } catch (err) {
      setError('Failed to load hikes. Please try again.');
      console.error('Error fetching hikes:', err);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(
        `/api/hikes/${selectedHikeId}/stats`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError('Failed to load statistics. Please try again.');
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const prepareChartData = () => {
    if (!stats || !stats.entries || stats.entries.length === 0) {
      return [];
    }

    // Calculate cumulative values
    let cumulativeKm = 0;
    let cumulativeMoney = 0;

    return stats.entries.map(entry => {
      cumulativeKm += entry.kmTravelled;
      cumulativeMoney += entry.moneySpent;

      return {
        date: formatDate(entry.date),
        kmTravelled: entry.kmTravelled,
        cumulativeKm: cumulativeKm,
        totalDistanceTarget: stats.hike.totalDistance, // Flat line for total distance
        rpe: entry.rpe,
        moneySpent: entry.moneySpent,
        cumulativeMoney: cumulativeMoney,
        budgetTarget: stats.hike.budget, // Flat line for budget
        mood: entry.mood,
        sleepQuality: entry.sleepQuality,
        overallFeeling: entry.overallFeeling,
        caloriesSpent: entry.caloriesSpent
      };
    });
  };

  if (!user) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Please log in to view hike reports.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Hike Report</h1>
        <div className="space-x-2">
          <button
            onClick={() => navigate('/hike/update')}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Add Entry
          </button>
          <button
            onClick={() => navigate('/hike/setup')}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            New Hike
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {hikes.length === 0 ? (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">No hikes found</p>
          <p className="text-sm">Create your first hike to start tracking your journey!</p>
          <button
            onClick={() => navigate('/hike/setup')}
            className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded"
          >
            Setup Your First Hike
          </button>
        </div>
      ) : (
        <>
          {/* Hike Selection */}
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="hikeSelect">
              Select Hike
            </label>
            <select
              id="hikeSelect"
              value={selectedHikeId}
              onChange={(e) => setSelectedHikeId(e.target.value)}
              className="shadow border rounded w-full md:w-1/2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            >
              {hikes.map(hike => (
                <option key={hike._id} value={hike._id}>
                  {hike.name}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading statistics...</p>
            </div>
          ) : stats ? (
            <>
              {/* Summary Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {/* Distance Card */}
                <div className="bg-white shadow-md rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Distance</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    {stats.stats.totalKmTravelled.toFixed(1)} km
                  </div>
                  <p className="text-sm text-gray-600">
                    of {stats.hike.totalDistance} km ({stats.stats.distancePercentage}%)
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${Math.min(stats.stats.distancePercentage, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Remaining: {stats.stats.distanceRemaining.toFixed(1)} km
                  </p>
                </div>

                {/* Budget Card */}
                <div className="bg-white shadow-md rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Budget</h3>
                  <div className="text-3xl font-bold text-green-600 mb-1">
                    ${stats.stats.totalMoneySpent.toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-600">
                    of ${stats.hike.budget.toFixed(2)} ({stats.stats.budgetPercentage}%)
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${Math.min(stats.stats.budgetPercentage, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Remaining: ${stats.stats.budgetRemaining.toFixed(2)}
                  </p>
                </div>

                {/* Entries Card */}
                <div className="bg-white shadow-md rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Entries</h3>
                  <div className="text-3xl font-bold text-purple-600 mb-1">
                    {stats.stats.totalEntries}
                  </div>
                  <p className="text-sm text-gray-600">
                    Days logged
                  </p>
                </div>

                {/* Daily Average Card */}
                <div className="bg-white shadow-md rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Daily Avg</h3>
                  <div className="text-xl font-bold text-orange-600 mb-1">
                    {stats.stats.totalEntries > 0
                      ? (stats.stats.totalKmTravelled / stats.stats.totalEntries).toFixed(1)
                      : '0'} km
                  </div>
                  <p className="text-sm text-gray-600 mb-1">Distance per day</p>
                  <div className="text-xl font-bold text-orange-600">
                    ${stats.stats.totalEntries > 0
                      ? (stats.stats.totalMoneySpent / stats.stats.totalEntries).toFixed(2)
                      : '0'}
                  </div>
                  <p className="text-sm text-gray-600">Spending per day</p>
                </div>
              </div>

              {/* Charts */}
              {stats.entries.length > 0 ? (
                <div className="space-y-8">
                  {/* Distance Chart - Cumulative with Target Line */}
                  <div className="bg-white shadow-md rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Cumulative Distance Travelled (km)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={prepareChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 'auto']} />
                        <Tooltip />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} />
                        <Line type="monotone" dataKey="cumulativeKm" stroke="#2563eb" strokeWidth={2} name="Cumulative KM" />
                        <Line type="monotone" dataKey="totalDistanceTarget" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" name="Total Distance Goal" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Money Spent Chart - Cumulative with Budget Line */}
                  <div className="bg-white shadow-md rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Cumulative Money Spent ($)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={prepareChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 'auto']} />
                        <Tooltip />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} />
                        <Line type="monotone" dataKey="cumulativeMoney" stroke="#16a34a" strokeWidth={2} name="Cumulative $ Spent" />
                        <Line type="monotone" dataKey="budgetTarget" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" name="Total Budget" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Calories Chart */}
                  <div className="bg-white shadow-md rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Calories Spent</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={prepareChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 'auto']} />
                        <Tooltip />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} />
                        <Line type="monotone" dataKey="caloriesSpent" stroke="#ea580c" strokeWidth={2} name="Calories" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Well-being Chart - Including RPE */}
                  <div className="bg-white shadow-md rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Well-being & Exertion Indicators (/10)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={prepareChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 10]} />
                        <Tooltip />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} />
                        <Line type="monotone" dataKey="rpe" stroke="#dc2626" strokeWidth={2} name="RPE (Exertion)" />
                        <Line type="monotone" dataKey="mood" stroke="#8b5cf6" strokeWidth={2} name="Mood" />
                        <Line type="monotone" dataKey="sleepQuality" stroke="#0891b2" strokeWidth={2} name="Sleep Quality" />
                        <Line type="monotone" dataKey="overallFeeling" stroke="#22c55e" strokeWidth={2} name="Overall Feeling" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <p className="text-sm text-gray-700">
                    <strong>No entries yet!</strong> Add your first entry to start seeing trends and statistics.
                  </p>
                </div>
              )}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export default HikeReport;